import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const emptyForm = { title: "", excerpt: "", body: "", category: "Press Release", location: "", published_at: "", published: true };

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/news?include_unpublished=true").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({
      title: n.title, excerpt: n.excerpt, body: n.body, category: n.category || "Press Release",
      location: n.location || "", published_at: (n.published_at || "").slice(0, 10), published: n.published,
    });
    setOpen(true);
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || !form.excerpt || !form.body) {
      toast.error("Title, excerpt, and body are required.");
      return;
    }
    setSaving(true);
    const payload = { ...form };
    if (payload.published_at) payload.published_at = new Date(payload.published_at).toISOString();
    else delete payload.published_at;
    try {
      if (editing) await api.put(`/news/${editing._id}`, payload);
      else await api.post("/news", payload);
      toast.success(editing ? "Article updated." : "Article published.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    try {
      await api.delete(`/news/${n._id}`);
      toast.success("Article deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-bg-primary">
      <header className="bg-bg-dark text-white">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="logo" className="h-9 brightness-0 invert" />
            <span className="font-heading text-lg hidden md:block">News CMS</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/60 text-sm hidden md:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 md:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-ink tracking-tight">Articles</h1>
            <p className="text-ink-soft text-sm mt-1">{items.length} total</p>
          </div>
          <button data-testid="new-article-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-brand-navy text-white font-semibold px-5 py-3 hover:bg-brand-red transition-colors">
            <Plus size={18} /> New Article
          </button>
        </div>

        <div className="bg-white border border-brand-navy/15" data-testid="admin-articles-table">
          {items.length === 0 && <p className="p-8 text-ink-soft">No articles yet.</p>}
          {items.map((n) => (
            <div key={n._id} className="flex items-center gap-4 p-5 border-b border-brand-navy/10 last:border-b-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="font-heading text-ink text-lg truncate">{n.title}</h3>
                  <span className={`text-xs px-2 py-0.5 shrink-0 ${n.published ? "bg-brand-teal/15 text-brand-navy" : "bg-brand-navy/10 text-ink-soft"}`}>
                    {n.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-ink-soft text-sm mt-1 truncate">{n.category} · {n.location || "—"}</p>
              </div>
              <a href={`/news/${n.slug}`} target="_blank" rel="noreferrer" className="text-ink-soft hover:text-brand-navy" title="View">
                <ExternalLink size={18} />
              </a>
              <button data-testid={`edit-${n._id}`} onClick={() => openEdit(n)} className="text-brand-navy hover:text-brand-teal" title="Edit">
                <Pencil size={18} />
              </button>
              <button data-testid={`delete-${n._id}`} onClick={() => remove(n)} className="text-ink-soft hover:text-brand-red" title="Delete">
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Article" : "New Article"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Labeled label="Title">
              <input data-testid="form-title" value={form.title} onChange={update("title")} className="w-full border border-brand-navy/20 px-3 py-2 focus:border-brand-teal focus:outline-none" />
            </Labeled>
            <div className="grid grid-cols-2 gap-4">
              <Labeled label="Category">
                <input data-testid="form-category" value={form.category} onChange={update("category")} className="w-full border border-brand-navy/20 px-3 py-2 focus:border-brand-teal focus:outline-none" />
              </Labeled>
              <Labeled label="Location">
                <input data-testid="form-location" value={form.location} onChange={update("location")} className="w-full border border-brand-navy/20 px-3 py-2 focus:border-brand-teal focus:outline-none" />
              </Labeled>
            </div>
            <Labeled label="Publish date">
              <input data-testid="form-date" type="date" value={form.published_at} onChange={update("published_at")} className="w-full border border-brand-navy/20 px-3 py-2 focus:border-brand-teal focus:outline-none" />
            </Labeled>
            <Labeled label="Excerpt">
              <textarea data-testid="form-excerpt" rows={2} value={form.excerpt} onChange={update("excerpt")} className="w-full border border-brand-navy/20 px-3 py-2 focus:border-brand-teal focus:outline-none resize-none" />
            </Labeled>
            <Labeled label="Body (separate paragraphs with a blank line)">
              <textarea data-testid="form-body" rows={8} value={form.body} onChange={update("body")} className="w-full border border-brand-navy/20 px-3 py-2 focus:border-brand-teal focus:outline-none resize-none" />
            </Labeled>
            <div className="flex items-center gap-3">
              <Switch data-testid="form-published" checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} />
              <span className="text-ink text-sm">Published</span>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 text-ink-soft hover:text-ink">Cancel</button>
            <button data-testid="save-article-btn" onClick={save} disabled={saving} className="bg-brand-navy text-white font-semibold px-6 py-2.5 hover:bg-brand-red transition-colors disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update" : "Publish"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="block text-ink-soft text-xs uppercase tracking-[0.15em] mb-2">{label}</label>
      {children}
    </div>
  );
}
